import axios from "axios";
import { COOKIES_NAME } from "constants/appConstants";
import { AuthTokens } from "services/auth";

const BASE_URL = window.configs.REACT_APP_API;

async function messageFromGetFileError(error: unknown): Promise<string> {
  if (!axios.isAxiosError(error) || !error.response) {
    return error instanceof Error
      ? error.message
      : "No se pudo obtener el archivo.";
  }
  const { status, data } = error.response;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text) as { message?: string | string[] };
      if (typeof parsed.message === "string") {
        return parsed.message;
      }
      if (Array.isArray(parsed.message)) {
        return parsed.message.join(", ");
      }
      if (text) {
        return text;
      }
    } catch {
      /* ignore parse errors */
    }
  } else if (data && typeof data === "object" && "message" in data) {
    const msg = (data as { message: string | string[] }).message;
    return Array.isArray(msg) ? msg.join(", ") : String(msg);
  }
  if (status === 403) {
    return "No tienes permiso para acceder a este archivo.";
  }
  return `Error al obtener el archivo (${status}).`;
}

export const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "multipart/form-data",
    "Refresh-Sent": "false",
  },
});

interface SendRequestP {
  method: "POST" | "GET" | "PATCH" | "DELETE" | "PUT" | "GET_FILE" | "POST_JSON" | "PUT_JSON";
  uri: string;
  params?: { [key: string]: string } | object;
  signal?: AbortSignal;
  images?: { [key: string]: File | File[] | null };
  fileType?: string;
  fileName?: string;
}

export async function sendRequest<T>({
  method,
  uri,
  params = {},
  signal,
  images,
  fileType,
  fileName,
}: SendRequestP): Promise<T> {
  switch (method) {
    case "POST": {
      const formData = new FormData();

      if (params) {
        Object.keys(params).forEach((key) => {
          const valueParam = (params as { [key: string]: any })[key];
          const typeParam = typeof valueParam;
          formData.append(
            key,
            typeParam == "string" || typeParam == "number"
              ? valueParam
              : JSON.stringify(valueParam),
          );
        });
      }

      if (images) {
        const keys = Object.keys(images);

        keys.forEach((ctKey) => {
          const keyData = images[ctKey];
          //keyData puede ser File o [File, File]

          if (Array.isArray(keyData) && keyData.length > 0) {
            keyData.forEach((ctImg, index) => {
              formData.append(`${ctKey}`, ctImg);
            });
          } else {
            formData.append(ctKey, keyData as File ?? "");
          }
        });
      }
      const response = await axiosInstance.post<T>(
        decodeURIComponent(uri),
        formData,
        { signal },
      );
      return response as T;
    }

    case "GET": {
      const response: T = await axiosInstance.get(uri, { params, signal });
      return response;
    }

    case "GET_FILE": {
      // Se agrega el header FileName para que el backend pueda identificar el archivo y hacer la comparacion, una capa mas de serguridad
      const rawTokens = sessionStorage.getItem(COOKIES_NAME) || null;
      if (!rawTokens) {
        throw new Error("No se encontró el token de autenticación");
      }
      const tokens: AuthTokens = JSON.parse(rawTokens || "");
      try {
        const response = await axios({
          method: "get",
          url: BASE_URL?.slice(0, -1) + uri,
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${tokens.accessToken}`,
            fileName: fileName!,
          },
        });

        const blob = response.data;
        // @ts-ignore
        return new File([blob], fileName, { type: fileType });
      } catch (err) {
        const message = await messageFromGetFileError(err);
        throw new Error(message);
      }
    }

    case "PUT": {
      const formData = new FormData();

      if (params) {
        Object.keys(params).forEach((key) => {
          const valueParam = (params as { [key: string]: any })[key];
          const typeParam = typeof valueParam;
          formData.append(
            key,
            typeParam == "string" || typeParam == "number"
              ? valueParam
              : JSON.stringify(valueParam),
          );
        });
      }

      if (images) {
        const keys = Object.keys(images);

        keys.forEach((ctKey) => {
          const keyData = images[ctKey];
          //keyData puede ser File o [File, File]

          if (Array.isArray(keyData) && keyData.length > 0) {
            keyData.forEach((ctImg, index) => {
              formData.append(`${ctKey}`, ctImg);
            });
          } else {
            formData.append(ctKey, keyData as File ?? "");
          }
        });
      }

      const response: T = await axiosInstance.put(uri, formData, { signal });
      return response;
    }

    case "DELETE": {
      return await axiosInstance.delete(uri, { data: params });
    }

    case "PATCH": {
      const response: T = await axiosInstance.patch(uri, params, { signal });
      return response;
    }

    case 'POST_JSON': {
      const response: T = await axiosInstance.post(uri, params, { 
        signal,
        headers: { 'Content-Type': 'application/json' }
      });
      return response;
    }

    case 'PUT_JSON': {
      // TODO: Log de desarrollo, eliminar luego de validar en DEV
      console.log('params',params);
      const response: T = await axiosInstance.put(uri, params, { 
        signal,
        headers: { 'Content-Type': 'application/json' }
      });
      return response;
    }

    default: {
      throw new Error("Falta method del fetch");
    }
  }
}
